import { useViewport } from '../../../../design-system/hooks/useViewport';
import {
  PageHeader,
  Select,
  Toolbar,
  ViewToggle,
} from '../../../../design-system/components/desktop';
import { Chevron, FilterButton, ResultBar } from '../../../../design-system/components/mobile';

/**
 * The band above the results: title, count, sort, view mode, and the way in to
 * the filters.
 *
 * WHY THIS BRANCHES
 * -----------------
 * It is two rows of the mobile-to-desktop map at once. On desktop the filters
 * live in a permanent rail, so all that belongs here is a toolbar of sort and
 * view controls. Below the handoff the filters are a bottom sheet, so this bar
 * has to carry the button that opens it, and it is a sticky `ResultBar` whose
 * job is to report the count while the grid scrolls under it. Different
 * controls, different DOM, exactly one tree mounted.
 *
 * WHAT WENT AWAY
 * --------------
 * Two hand-inlined 18px filter SVGs and a third for sort, a bespoke
 * `.sort-select`, a bespoke `.view-controls` pair, and 397 lines of stylesheet.
 * Sort is now `Select`, view mode is `ViewToggle` (whose chosen half fills with
 * INK, because swapping the view is a command), and the filter entry point is
 * the library's `FilterButton` with its ink applied-count pill.
 *
 * Also gone: the `cartOpen` / `setCartOpen` props, threaded in from the page
 * and never read, and the unused `CartOverlay` and `FilterModal` imports behind
 * them. The cart lives in the shell's nav on both tracks.
 */
const MarketplaceHeader = ({
  title = 'Marketplace',
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
  onOpenFilters,
  onOpenSort,
  appliedCount = 0,
  totalCards,
  countLabel = 'cards',
  /* Only /search passes this, and only the mobile branch reads it — the desktop
     branch already carries the query in the PageHeader subtitle it renders. */
  query,
  sortOptions = [],
  /* `/marketplace` renders its own title inside the filter rail, so the band
     there is controls only. Every other browse surface still wants the H1 and
     the count above the toolbar. */
  showTitle = true,
}) => {
  const { isDesktop } = useViewport();
  const count = totalCards == null ? null : Number(totalCards).toLocaleString('en-ZA');

  if (isDesktop) {
    return (
      <>
        {showTitle && (
          <PageHeader title={title} sub={count == null ? undefined : `${count} ${countLabel}`} />
        )}
        {/* `.dsa-browse-bar` in app-layer.css. `Toolbar` is vendored as table
            chrome and pads itself 24px in to clear a `.dsk-table-wrap` edge;
            here it is a bare page band, so it has to align to the page gutter
            like the H1 above it and the filter rail below it.

            SORT SITS WITH THE VIEW TOGGLE, not at the far left. Both are
            controls over the same result set, and split across ~900px of empty
            band they read as two unrelated things and cost a whole row of
            header height to say it. One cluster, right aligned. */}
        <Toolbar className="dsa-browse-bar">
          <div className="dsa-browse-bar__controls">
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={onSortChange}
              placeholder="Sort"
              label="Sort by"
            />
            <ViewToggle value={viewMode} onChange={onViewModeChange} />
          </div>
        </Toolbar>
      </>
    );
  }

  const activeSort = sortOptions.find((option) => option.value === sortBy);

  /* TWO LINES BELOW THE HANDOFF, and the split is the whole point.
     `.ds-resultbar` is a single flex row, and the five things it was asked to
     hold measured ~473px inside the 358px a 390px phone has after gutters — so
     the sort label and "Filters" both wrapped inside their pills and the fixed
     width view toggle pushed the line off the right edge. The measurement, and
     why the pair that stays together is sort + Filters, is on `.dsa-mbar` in
     patterns/market/market.css.

     ResultBar spreads `trailing` straight into its own root, so a fragment
     here puts the toggle and the control line in as SIBLINGS of the count,
     which is what lets the grid there place all three. A wrapper element could
     not, because the count is ResultBar's child and not ours. */
  return (
    <ResultBar
      className="dsa-mbar"
      count={count == null ? '' : count}
      /* The count's trailing qualifier is the one slot on this bar with room to
         say what was searched, and it is already the lighter `<small>` that a
         qualifier belongs in — so "12 cards for “vmax”", matching the desktop
         subtitle word for word. */
      countLabel={
        count == null ? undefined : query ? `${countLabel} for “${query}”` : countLabel
      }
      trailing={
        <>
          {/* GRID OR ROWS ON THE PHONE TOO. The choice existed only on desktop,
              which is backwards: a 2-up grid of art and a stack of rows with a
              price column are further apart on a phone than four columns and
              rows are on a monitor.
              This is `dsk-viewtoggle` on the mobile track — one of the handful
              of deliberate prefix crossings, alongside `Skeleton` and `Empty`,
              because the control is icon-only and the library ships no `ds-`
              equivalent. It belongs upstream as `.ds-viewtoggle`. */}
          <ViewToggle
            className="dsa-mbar__view"
            value={viewMode}
            onChange={onViewModeChange}
          />
          <div className="dsa-mbar__line">
            {/* Sort opens a sheet rather than a native select: below the handoff
                a select popup is the OS's surface, not ours, and the sheet is
                the mobile track's answer to every picker.

                The label is wrapped in its own span because the button is a
                flex row and the label is the ONLY part of it allowed to give
                up width — a bare text node cannot carry `text-overflow`. */}
            <button
              type="button"
              className="ds-filterbtn dsa-mbar__sort"
              onClick={onOpenSort}
            >
              <span>{activeSort ? activeSort.label : 'Sort'}</span>
              <Chevron direction="down" width={16} height={16} />
            </button>
            <FilterButton
              className="dsa-mbar__filters"
              count={appliedCount}
              onClick={onOpenFilters}
            />
          </div>
        </>
      }
    />
  );
};

export default MarketplaceHeader;
